if (!customElements.get('check-delivery')) {
  /* ── Config ──────────────────────────────────────────────────────────────── */

  const CD_CONFIG = Object.freeze({
    storageKey: 'bonkers_delivery_pincode',
    apiUrl: 'https://n72i4u4sv1.execute-api.ap-south-1.amazonaws.com/desolatebonkersapp/check-edd',
    timeout: 12000,
  });

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* ── Helpers ─────────────────────────────────────────────────────────────── */

  /**
   * Returns a human-readable date range string from ISO date strings (YYYY-MM-DD).
   *   Same date  → "13 Jul"
   *   Same month → "13 - 14 Jul"
   *   Diff month → "28 Jun - 2 Jul"
   */
  function getDateLabel(minEdd, maxEdd) {
    // Parse as local date to avoid UTC-offset issues (append T00:00 forces local)
    const minDate = new Date(minEdd + 'T00:00');
    const maxDate = new Date(maxEdd + 'T00:00');

    const minDay = minDate.getDate();
    const minMonth = MONTHS[minDate.getMonth()];
    const maxDay = maxDate.getDate();
    const maxMonth = MONTHS[maxDate.getMonth()];

    // Same date — show a single clean date, not "13 - 13 Jul"
    if (minEdd === maxEdd) {
      return `${minDay} ${minMonth}`;
    }

    if (minMonth === maxMonth) {
      return `${minDay} - ${maxDay} ${minMonth}`;
    }
    return `${minDay} ${minMonth} - ${maxDay} ${maxMonth}`;
  }

  /** Safe localStorage helpers — gracefully handles private-mode restrictions. */
  const storage = {
    get(key) { try { return localStorage.getItem(key) ?? ''; } catch { return ''; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch { /* noop */ } },
    remove(key) { try { localStorage.removeItem(key); } catch { /* noop */ } },
  };

  /** Strips leading "U-" / "u-" or "A-" / "a-" prefix from SKU codes (e.g. "U-890448" or "A-890448" → "890448"). */
  function cleanSku(sku) {
    return sku.replace(/^[ua]-/i, '');
  }

  /* ── Custom Element ──────────────────────────────────────────────────────── */

  class CheckDelivery extends HTMLElement {
    constructor() {
      super();

      // Bind methods that are used as event listeners so we can remove them later
      this._onInput = this._handleInput.bind(this);
      this._onBtnClick = this._handleBtnClick.bind(this);
      this._onVariantChange = this._handleVariantChange.bind(this);
    }

    connectedCallback() {
      // Query scoped child elements
      this.inputEl = this.querySelector('.check-delivery-block__input');
      this.btnEl = this.querySelector('.check-delivery-block__btn');
      this.messageEl = this.querySelector('.check-delivery-block__message');
      this.messageTextEl = this.querySelector('.check-delivery-block__message-text');
      this.truckEl = this.querySelector('.check-delivery-block__truck-icon');
      this.checkingEl = this.querySelector('.check-delivery-block__checking');
      this.errorEl = this.querySelector('.check-delivery-block__error');

      // Guard: HTML incomplete
      if (!this.inputEl || !this.btnEl || !this.messageEl || !this.truckEl) return;

      // Read customizer settings from data attributes
      this.emptyMessage = this.dataset.emptyMessage || 'Please enter PIN code to check delivery time';
      this.deliveryPrefix = this.dataset.deliveryPrefix || 'Expected delivery between';

      // Read initial product identifiers from data attributes
      this.sku = (this.dataset.sku ?? '').trim();
      this.productId = (this.dataset.productId ?? '').trim();
      this.variantId = (this.dataset.variantId ?? '').trim();

      // In-flight request controller
      this._abortController = null;

      // State machine: 'empty' | 'partial' | 'checking' | 'checked' | 'error'
      this._currentState = 'empty';

      // Attach local event listeners
      this.inputEl.addEventListener('input', this._onInput);
      this.btnEl.addEventListener('click', this._onBtnClick);

      // Listen for variant changes (global custom event dispatched by the theme)
      document.addEventListener('product:variant-change', this._onVariantChange);

      // Auto-fill from localStorage
      // Always run check if a saved pincode exists — _runCheck handles OOS gracefully
      const saved = storage.get(CD_CONFIG.storageKey);
      if (/^\d{6}$/.test(saved)) {
        this.inputEl.value = saved;
        this._runCheck(saved);
      } else {
        this._setState('empty');
      }
    }

    disconnectedCallback() {
      // Clean up all listeners to prevent memory leaks
      this.inputEl?.removeEventListener('input', this._onInput);
      this.btnEl?.removeEventListener('click', this._onBtnClick);
      document.removeEventListener('product:variant-change', this._onVariantChange);
      this._abortInflight();
    }

    /* ── Variant change handler ────────────────────────────────────────────── */

    _handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      const wasOos = this.classList.contains('is-oos');
      const isOos = !variant.available;

      if (isOos) {
        this.classList.add('is-oos');
      } else {
        this.classList.remove('is-oos');
      }

      const newSku = (variant.sku ?? '').trim();
      const skuChanged = newSku !== this.sku;

      this.sku = newSku;
      this.dataset.sku = newSku;

      // Keep product/variant IDs in sync with the selected variant
      this.variantId = String(variant.id ?? '').trim();
      this.dataset.variantId = this.variantId;

      // Re-check delivery if a valid pincode is present
      if (wasOos || skuChanged) {
        const pincode = this.inputEl.value.replace(/\D/g, '');
        if (pincode.length === 6) {
          this._runCheck(pincode);
        }
      }
    }

    /* ── Input handler ─────────────────────────────────────────────────────── */

    _handleInput() {
      // Strip non-digits, enforce max 6 chars
      const raw = this.inputEl.value.replace(/\D/g, '').slice(0, 6);
      this.inputEl.value = raw;

      if (raw.length === 0) {
        this._setState('empty');
      } else if (raw.length < 6) {
        this._setState('partial');
      } else {
        this._runCheck(raw); // exactly 6 → fire API
      }
    }

    /* ── Button handler ────────────────────────────────────────────────────── */

    _handleBtnClick() {
      if (this._currentState === 'checked' || this._currentState === 'error') {
        this.inputEl.value = '';
        storage.remove(CD_CONFIG.storageKey);
        this._setState('empty');
        this.inputEl.focus();
      }
    }

    /* ── State machine ─────────────────────────────────────────────────────── */

    _setState(state, label = '', prefixOverride = null) {
      this._currentState = state;

      // Reset feedback elements (message, checking, error) — truck stays always visible
      this.messageEl.classList.remove('cd--visible');
      this.checkingEl.classList.remove('cd--visible');
      this.errorEl.classList.remove('cd--visible');

      if (state === 'checked') {
        this.classList.add('is-checked');
      } else {
        this.classList.remove('is-checked');
      }

      this.btnEl.disabled = false;
      this.btnEl.textContent = 'Check';

      switch (state) {
        case 'empty':
          this.btnEl.disabled = true;
          if (this.messageTextEl) this.messageTextEl.innerHTML = this.emptyMessage;
          this.messageEl.classList.add('cd--visible');
          break;

        case 'partial':
          if (this.messageTextEl) this.messageTextEl.innerHTML = this.emptyMessage;
          this.messageEl.classList.add('cd--visible');
          break;

        case 'checking':
          this.btnEl.disabled = true;
          this.checkingEl.classList.add('cd--visible');
          break;

        case 'checked':
          this.btnEl.textContent = 'Change';
          if (this.messageTextEl) {
            const prefix = prefixOverride || this.deliveryPrefix;
            this.messageTextEl.innerHTML = `<span class="check-delivery-block__message-prefix">${prefix}</span> <span class="check-delivery-block__message-date">${label}</span>`;
          }
          this.messageEl.classList.add('cd--visible');
          break;

        case 'error':
          this.btnEl.textContent = 'Retry';
          this.errorEl.classList.add('cd--visible');
          break;

        case 'hidden':
          // Entire SLA block is hidden, leaving only the nearby-stores widget visible in the body
          this.btnEl.textContent = 'Change';
          break;
      }
    }

    /* ── API runner ─────────────────────────────────────────────────────────── */

    async _runCheck(pincode) {
      // Abort any in-flight request before starting a new one
      this._abortInflight();

      storage.set(CD_CONFIG.storageKey, pincode);

      // Fire the global event so nearby-stores.js triggers its fetch independently of SLA
      document.dispatchEvent(new CustomEvent('bonkers:pincode-updated', { detail: { pincode } }));

      // If product is OOS, skip SLA fetching because it won't be serviceable.
      // Hide the SLA UI completely so only the nearby-stores checking/response is visible.
      if (this.classList.contains('is-oos')) {
         this._setState('hidden');
         return; 
      }

      this._setState('checking');

      this._abortController = new AbortController();

      // Timeout wrapper
      const timeoutId = setTimeout(() => this._abortController?.abort(), CD_CONFIG.timeout);

      try {
        const { minEdd, maxEdd } = await this._fetchEdd(pincode, this._abortController.signal);
        const dateLabel = getDateLabel(minEdd, maxEdd);
        const prefixOverride = minEdd === maxEdd ? 'Expected delivery by' : null;
        this._setState('checked', dateLabel, prefixOverride);
      } catch (err) {
        // Silently ignore aborted requests (user changed variant or typed new pincode)
        if (err.name === 'AbortError') return;
        this._setState('error');
      } finally {
        clearTimeout(timeoutId);
        this._abortController = null;
      }
    }

    /**
     * Calls the check-edd API.
     * Returns minEdd and maxEdd (ISO date strings, e.g. "2026-07-13") from the top-level response.
     * Throws if the response status is not 200 or dates are missing.
     *
     * @param {string} pincode
     * @param {AbortSignal} signal
     * @returns {Promise<{minEdd: string, maxEdd: string}>}
     */
    async _fetchEdd(pincode, signal) {
      const payload = {
        pincode,
        items: [
          {
            sku_id: cleanSku(this.sku),
            product_id: this.productId,
            variant_id: this.variantId,
            quantity: 1,
          },
        ],
      };

      const response = await fetch(CD_CONFIG.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      if (data?.status !== 200) throw new Error('API returned failure');

      const minEdd = data?.data?.min_edd;
      const maxEdd = data?.data?.max_edd;

      if (!minEdd || !maxEdd) throw new Error('EDD dates missing in response');

      return { minEdd, maxEdd };
    }

    /* ── Utilities ─────────────────────────────────────────────────────────── */

    _abortInflight() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }
  }

  customElements.define('check-delivery', CheckDelivery);
}